import { format, isToday, isYesterday } from 'date-fns';
import {
  IconMail,
  IconEye,
  IconHandClick,
  IconAlertCircle,
  IconBellOff,
  IconShieldExclamation,
  IconActivity,
  IconPhone,
  IconUsers,
  IconMailForward,
  IconMessage,
  IconNote,
  IconMailbox,
} from '@tabler/icons-react';

/** Manual entries are visually distinct from anything SendGrid reports. */
const MANUAL_CLASS_NAME = 'bg-purple-50 text-purple-500';

/**
 * Display metadata per outreach medium for manually-logged activity. `medium` is
 * optional, so an entry with none falls back to a neutral "Activity logged".
 */
export const MEDIUM_TYPES = {
  phone: { label: 'Phone call', Icon: IconPhone, className: MANUAL_CLASS_NAME },
  email: { label: 'Email sent', Icon: IconMailForward, className: MANUAL_CLASS_NAME },
  meeting: { label: 'Meeting', Icon: IconUsers, className: MANUAL_CLASS_NAME },
  // Not IconMail: that is the icon for a SendGrid 'delivered' row, and a purple
  // envelope next to a grey one reads as the same thing twice.
  letter: { label: 'Letter sent', Icon: IconMailbox, className: MANUAL_CLASS_NAME },
  text: { label: 'Text message', Icon: IconMessage, className: MANUAL_CLASS_NAME },
  other: { label: 'Activity logged', Icon: IconNote, className: MANUAL_CLASS_NAME },
};

/**
 * Options for the composer's medium select, in the order they are offered.
 *
 * The "none" sentinel is deliberate rather than an empty string: `SelectInput`
 * renders an option whose value is `''` as disabled (it treats that as a
 * placeholder), which would make an optional field impossible to clear once set.
 * It maps to null on the way to the API.
 */
export const NO_MEDIUM = 'none';

export const MEDIUM_OPTIONS = [
  { value: NO_MEDIUM, label: 'Not specified' },
  // Labels match the timeline row labels exactly: picking "Phone call" should
  // not produce a row that says something else.
  { value: 'phone', label: 'Phone call' },
  { value: 'email', label: 'Email sent' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'letter', label: 'Letter sent' },
  { value: 'text', label: 'Text message' },
  { value: 'other', label: 'Activity logged' },
];

/**
 * Display metadata per stored SendGrid event type. `event` values come straight
 * from SendGrid via email-events-api, so anything unrecognised falls back to a
 * neutral row rather than disappearing from the timeline.
 */
export const EVENT_TYPES = {
  delivered: { label: 'Email delivered', Icon: IconMail, className: 'bg-gray-10 text-gray-600' },
  processed: { label: 'Email sent', Icon: IconMail, className: 'bg-gray-10 text-gray-600' },
  open: { label: 'Opened email', Icon: IconEye, className: 'bg-primary-50 text-primary-600' },
  click: { label: 'Clicked a link', Icon: IconHandClick, className: 'bg-green-50 text-green-700' },
  bounce: { label: "Couldn't be delivered", Icon: IconAlertCircle, className: 'bg-red-50 text-red-500' },
  blocked: {
    label: 'Blocked by their email provider',
    Icon: IconAlertCircle,
    className: 'bg-red-50 text-red-500',
  },
  dropped: { label: 'Not sent', Icon: IconAlertCircle, className: 'bg-red-50 text-red-500' },
  deferred: {
    label: 'Delivery delayed',
    Icon: IconAlertCircle,
    className: 'bg-orange-50 text-orange-600',
  },
  unsubscribe: { label: 'Unsubscribed', Icon: IconBellOff, className: 'bg-orange-50 text-orange-600' },
  group_unsubscribe: {
    label: 'Unsubscribed from a group',
    Icon: IconBellOff,
    className: 'bg-orange-50 text-orange-600',
  },
  group_resubscribe: {
    label: 'Resubscribed to a group',
    Icon: IconBellOff,
    className: 'bg-green-50 text-green-700',
  },
  spam_report: {
    label: 'Marked as spam',
    Icon: IconShieldExclamation,
    className: 'bg-purple-50 text-purple-500',
  },
};

const FALLBACK_TYPE = { Icon: IconActivity, className: 'bg-gray-10 text-gray-600' };

// 'spam_report' -> 'Spam report'
const humanizeEvent = (event) => {
  if (!event) return 'Activity';
  const spaced = event.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const getEventType = (event) => {
  const type = EVENT_TYPES[event];
  if (type) return type;
  return { ...FALLBACK_TYPE, label: humanizeEvent(event) };
};

/** True for a manually-logged entry, which is the only kind a user can edit. */
export const isManual = (entry) => entry?.source === 'manual';

/**
 * Display metadata for any timeline row, whichever table it came from.
 */
export const getEntryType = (entry) => {
  if (!isManual(entry)) return getEventType(entry?.event);
  return MEDIUM_TYPES[entry.medium] ?? MEDIUM_TYPES.other;
};

export const getDayLabel = (date) => {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'd MMM yyyy');
};

/**
 * Bounce reasons arrive straight from the receiving mail server, prefixed with
 * an SMTP code — "552 5.2.2 mailbox full". The codes mean nothing to a
 * fundraiser, so drop them and leave the sentence.
 */
export const formatReason = (reason) => {
  if (!reason) return null;

  const withoutCodes = reason
    .trim()
    // Leading status code, optionally followed by an enhanced status code.
    .replace(/^\d{3}[\s-]*(\d\.\d\.\d)?\s*/, '')
    .trim();

  const cleaned = withoutCodes || reason.trim();

  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

/**
 * Clicked URLs carry campaign tracking query strings that read as noise. Show
 * the host and path; the full URL stays on the link's href and tooltip.
 */
export const formatUrl = (url) => {
  if (!url) return null;

  try {
    const parsed = new URL(url);
    const path = parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/$/, '');
    return `${parsed.host}${path}`;
  } catch {
    return url;
  }
};

/**
 * Group events into consecutive day buckets. The API returns them newest first,
 * so a single pass preserves that order without re-sorting.
 */
export const groupEventsByDay = (events) =>
  events.reduce((days, event) => {
    const date = new Date(event.timestamp);
    const label = getDayLabel(date);
    const currentDay = days[days.length - 1];

    if (currentDay?.label === label) {
      currentDay.events.push(event);
    } else {
      days.push({ label, events: [event] });
    }

    return days;
  }, []);
